import type { UploadEvidenceData } from 'api';
import { logger } from 'monitor';
import { create } from 'zustand';

import { buildEvidenceMetadataFromExif, extractExifData } from '../utils/exif/exif';

const DB_NAME = 'offlineEvidenceDB';
const DB_VERSION = 1;
const METADATA_STORE = 'evidenceMetadata';
const FILES_STORE = 'evidenceFiles';

export interface OfflineEvidenceFile extends Omit<UploadEvidenceData, 'photo' | 'onProgress'> {
  offlineId: string;
  photoMeta: {
    name: string;
    size: number;
    type: string;
    lastModified: number;
  };
}

const generateOfflineId = (file: UploadEvidenceData): string =>
  `${file.photo.name}-${file.photo.size}-${file.startTime}-${Date.now()}`.replace(/\s+/g, '').trim();

const openDatabase = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(METADATA_STORE)) {
        db.createObjectStore(METADATA_STORE, { keyPath: 'offlineId' });
      }

      if (!db.objectStoreNames.contains(FILES_STORE)) {
        db.createObjectStore(FILES_STORE, { keyPath: 'offlineId' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      logger.error('Failed to open IndexedDB', request.error);
      reject(request.error);
    };
  });
};

const storeEvidenceFile = async (metadata: OfflineEvidenceFile, file: File): Promise<void> => {
  logger.info('Persisting offline evidence file to IndexedDB', {
    fileName: metadata.photoMeta.name,
    fileSize: file.size,
    offlineId: metadata.offlineId,
    parentId: metadata.parentId,
  });

  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([METADATA_STORE, FILES_STORE], 'readwrite');

    const metadataStore = transaction.objectStore(METADATA_STORE);
    const metadataRequest = metadataStore.put(metadata);

    metadataRequest.onerror = () => {
      logger.error('Failed to store evidence metadata', metadataRequest.error);
      reject(metadataRequest.error);
    };

    const filesStore = transaction.objectStore(FILES_STORE);
    const fileRequest = filesStore.put({ file, offlineId: metadata.offlineId });

    fileRequest.onerror = () => {
      logger.error('Failed to store evidence file', fileRequest.error);
      reject(fileRequest.error);
    };

    transaction.oncomplete = () => {
      logger.info('Offline evidence file persisted to IndexedDB', {
        offlineId: metadata.offlineId,
        parentId: metadata.parentId,
      });
      resolve();
    };
    transaction.onerror = () => {
      logger.error('Transaction failed', transaction.error);
      reject(transaction.error);
    };
  });
};

const getAllEvidenceMetadata = async (): Promise<OfflineEvidenceFile[]> => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([METADATA_STORE], 'readonly');
    const store = transaction.objectStore(METADATA_STORE);
    const request = store.getAll();

    request.onsuccess = () => {
      logger.info('Loaded offline evidence metadata from IndexedDB', {
        count: request.result?.length ?? 0,
      });
      resolve(request.result || []);
    };
    request.onerror = () => {
      logger.error('Failed to get evidence metadata', request.error);
      reject(request.error);
    };
  });
};

const getFileById = async (offlineId: string): Promise<File | null> => {
  logger.debug('Loading offline evidence file blob from IndexedDB', { offlineId });
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([FILES_STORE], 'readonly');
    const store = transaction.objectStore(FILES_STORE);
    const request = store.get(offlineId);

    request.onsuccess = () => {
      if (request.result) {
        logger.debug('Loaded offline evidence file blob from IndexedDB', { offlineId });
        resolve(request.result.file);
      } else {
        logger.warn('Offline evidence file blob was not found in IndexedDB', { offlineId });
        resolve(null);
      }
    };

    request.onerror = () => {
      logger.error('Failed to get file', request.error);
      reject(request.error);
    };
  });
};

const removeEvidenceFile = async (offlineId: string): Promise<void> => {
  logger.info('Deleting offline evidence file from IndexedDB', { offlineId });
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([METADATA_STORE, FILES_STORE], 'readwrite');

    const metadataStore = transaction.objectStore(METADATA_STORE);
    metadataStore.delete(offlineId);

    const filesStore = transaction.objectStore(FILES_STORE);
    filesStore.delete(offlineId);

    transaction.oncomplete = () => {
      logger.info('Deleted offline evidence file from IndexedDB', { offlineId });
      resolve();
    };
    transaction.onerror = () => {
      logger.error('Failed to remove evidence file', transaction.error);
      reject(transaction.error);
    };
  });
};

const clearAllEvidenceFiles = async (): Promise<void> => {
  logger.info('Clearing all offline evidence files from IndexedDB');
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([METADATA_STORE, FILES_STORE], 'readwrite');

    const metadataStore = transaction.objectStore(METADATA_STORE);
    metadataStore.clear();

    const filesStore = transaction.objectStore(FILES_STORE);
    filesStore.clear();

    transaction.oncomplete = () => {
      logger.info('Cleared all offline evidence files from IndexedDB');
      resolve();
    };
    transaction.onerror = () => {
      logger.error('Failed to clear evidence files', transaction.error);
      reject(transaction.error);
    };
  });
};

interface PhotoWithObjectUrl {
  id: string;
  url: string;
}

interface StoreState {
  evidenceFiles: OfflineEvidenceFile[];
  objectUrls: Record<string, PhotoWithObjectUrl[]>;

  addEvidenceFile: (file: UploadEvidenceData, fileId?: string) => Promise<string>;
  getEvidenceFiles: () => OfflineEvidenceFile[];
  getEvidenceFileById: (offlineId: string) => OfflineEvidenceFile | undefined;
  getEvidenceFilesByParentId: (parentId: string) => OfflineEvidenceFile[];
  getPhotoFromOfflineFile: (offlineFile: OfflineEvidenceFile['offlineId']) => Promise<File | null>;
  getPhotoObjectUrlsByParentId: (parentId: string) => Promise<PhotoWithObjectUrl[]>;
  removeEvidenceFile: (offlineId: string) => Promise<void>;
  clearEvidenceFiles: () => Promise<void>;
  cleanupObjectUrls: (parentId: string) => void;
  initializeStore: () => Promise<void>;
}

export const useOfflineEvidenceStore = create<StoreState>((set, get) => {
  let hasLocalMutation = false;

  getAllEvidenceMetadata()
    .then((metadata) => {
      if (hasLocalMutation) {
        logger.info('Skipping offline evidence store hydration because local state changed first', {
          inMemoryCount: get().evidenceFiles.length,
          indexedDbCount: metadata.length,
        });
        return;
      }

      logger.info('Offline evidence store initialized from IndexedDB', { count: metadata.length });
      set({ evidenceFiles: metadata });
    })
    .catch((error) => {
      logger.error('Failed to initialize offline evidence store from IndexedDB', error);
    });

  return {
    addEvidenceFile: async (file: UploadEvidenceData, fileId?: string) => {
      try {
        const offlineId = fileId ?? generateOfflineId(file);
        logger.info('Adding offline evidence file to store', {
          evidenceType: file.evidenceType,
          fileName: file.photo.name,
          fileSize: file.photo.size,
          offlineId,
          parentId: file.parentId,
        });
        const exifData = await extractExifData(file.photo);
        logger.info('Offline evidence EXIF extracted', {
          hasDeviceName: Boolean(exifData.deviceName),
          hasMake: Boolean(exifData.make),
          hasModel: Boolean(exifData.model),
          hasTimestamp: Boolean(exifData.timestamp),
          offlineId,
          parentId: file.parentId,
        });

        const metadata: OfflineEvidenceFile = {
          evidenceType: file.evidenceType,
          metadata: buildEvidenceMetadataFromExif(exifData, file.metadata),
          model: file.model,
          offlineId,
          parentId: file.parentId,
          photoMeta: {
            lastModified: file.photo.lastModified,
            name: file.photo.name,
            size: file.photo.size,
            type: file.photo.type,
          },
          startTime: file.startTime,
        };

        await storeEvidenceFile(metadata, file.photo);

        hasLocalMutation = true;
        set((state: StoreState) => ({
          evidenceFiles: [...state.evidenceFiles, metadata],
        }));
        logger.info('Offline evidence metadata added to in-memory store', {
          offlineId,
          parentId: file.parentId,
        });

        const parentId = String(file.parentId);

        const photo = await getFileById(offlineId);
        if (photo) {
          const url = URL.createObjectURL(photo);
          const newPhotoUrl = { id: offlineId, url };

          const existingUrls = get().objectUrls[parentId] || [];

          hasLocalMutation = true;
          set((state: StoreState) => ({
            objectUrls: {
              ...state.objectUrls,
              [parentId]: [...existingUrls, newPhotoUrl],
            },
          }));
          logger.info('Offline evidence object URL created', {
            offlineId,
            parentId,
          });
        } else {
          logger.warn('Offline evidence object URL was not created because the blob was missing', {
            offlineId,
            parentId,
          });
        }

        logger.info('Offline evidence file added to store successfully', {
          offlineId,
          parentId: file.parentId,
        });
        return offlineId;
      } catch (error) {
        logger.error('Failed to add evidence file', error);
        throw error;
      }
    },

    cleanupObjectUrls: (parentId: string) => {
      const urls = get().objectUrls[parentId] || [];
      logger.info('Cleaning up offline evidence object URLs', {
        count: urls.length,
        parentId,
      });

      urls.forEach((photo) => {
        URL.revokeObjectURL(photo.url);
      });

      hasLocalMutation = true;
      set((state: StoreState) => {
        const { [parentId]: _, ...rest } = state.objectUrls;
        return { objectUrls: rest };
      });
    },

    clearEvidenceFiles: async () => {
      try {
        logger.info('Clearing offline evidence store', {
          evidenceFilesCount: get().evidenceFiles.length,
          objectUrlParentCount: Object.keys(get().objectUrls).length,
        });
        await clearAllEvidenceFiles();

        Object.keys(get().objectUrls).forEach(get().cleanupObjectUrls);

        hasLocalMutation = true;
        set({ evidenceFiles: [], objectUrls: {} });
        logger.info('Offline evidence store cleared');
      } catch (error) {
        logger.error('Failed to clear evidence files', error);
        throw error;
      }
    },

    evidenceFiles: [],

    getEvidenceFileById: (offlineId: string) => get().evidenceFiles.find((file) => file.offlineId === offlineId),

    getEvidenceFiles: () => get().evidenceFiles,

    getEvidenceFilesByParentId: (parentId: string) =>
      get().evidenceFiles.filter((file) => String(file.parentId) === String(parentId)),

    getPhotoFromOfflineFile: async (offlineFileId: OfflineEvidenceFile['offlineId']) => {
      try {
        logger.debug('Offline evidence photo requested from store', { offlineFileId });
        return await getFileById(offlineFileId);
      } catch (error) {
        logger.error('Failed to get photo from offline file', error, {
          offlineFileId,
        });
        return null;
      }
    },

    getPhotoObjectUrlsByParentId: async (parentId: string) => {
      const stringParentId = String(parentId);
      logger.info('Offline evidence object URLs requested', { parentId: stringParentId });

      const offlineFiles = get().getEvidenceFilesByParentId(stringParentId);

      const existingUrls = get().objectUrls[stringParentId] || [];
      const existingIds = existingUrls.map((url) => url.id);

      const newFiles = offlineFiles.filter((file) => !existingIds.includes(file.offlineId));

      if (newFiles.length === 0 && existingUrls.length === offlineFiles.length && existingUrls.length > 0) {
        logger.info('Reusing existing offline evidence object URLs', {
          count: existingUrls.length,
          parentId: stringParentId,
        });
        return existingUrls;
      }

      const newPhotoUrls = await Promise.all(
        newFiles.map(async (file) => {
          const photo = await get().getPhotoFromOfflineFile(file.offlineId);
          if (photo) {
            return {
              id: file.offlineId,
              url: URL.createObjectURL(photo),
            };
          }
          return null;
        })
      );

      const validNewPhotoUrls = newPhotoUrls.filter(Boolean) as PhotoWithObjectUrl[];

      const allPhotoUrls = offlineFiles
        .map((file) => {
          const newUrl = validNewPhotoUrls.find((url) => url.id === file.offlineId);
          if (newUrl) return newUrl;

          const existingUrl = existingUrls.find((url) => url.id === file.offlineId);
          if (existingUrl) return existingUrl;

          logger.warn('Missing URL for offline file', {
            fileName: file.photoMeta.name,
            offlineId: file.offlineId,
          });
          return null;
        })
        .filter(Boolean) as PhotoWithObjectUrl[];

      logger.info('Generated photo URLs', {
        count: allPhotoUrls.length,
        existingUrlsCount: existingUrls.length,
        newUrlsCount: validNewPhotoUrls.length,
        offlineFilesCount: offlineFiles.length,
        parentId: stringParentId,
      });

      set((state: StoreState) => ({
        objectUrls: {
          ...state.objectUrls,
          [stringParentId]: allPhotoUrls,
        },
      }));

      return allPhotoUrls;
    },

    initializeStore: async () => {
      try {
        logger.info('Offline evidence store manual initialization started');
        const metadata = await getAllEvidenceMetadata();
        set({ evidenceFiles: metadata });
        logger.info('Offline evidence store manual initialization completed', { count: metadata.length });
      } catch (error) {
        logger.error('Failed to initialize store', error);
        set({ evidenceFiles: [] });
      }
    },

    objectUrls: {},

    removeEvidenceFile: async (offlineId: string) => {
      logger.info('Removing offline evidence file from store', { offlineId });
      try {
        const file = get().getEvidenceFileById(offlineId);
        if (!file) {
          logger.warn('Offline evidence file removal skipped because metadata was not found', { offlineId });
          return;
        }

        const parentId = String(file.parentId);
        await removeEvidenceFile(offlineId);

        hasLocalMutation = true;
        set((state: StoreState) => ({
          evidenceFiles: state.evidenceFiles.filter((f) => f.offlineId !== offlineId),
        }));
        logger.info('Offline evidence metadata removed from in-memory store', { offlineId, parentId });

        const urls = get().objectUrls[parentId];
        if (!urls) {
          logger.info('No offline evidence object URLs to update after removal', { offlineId, parentId });
          return;
        }

        const url = urls.find((u) => u.id === offlineId);
        if (url) {
          URL.revokeObjectURL(url.url);

          hasLocalMutation = true;
          set((state: StoreState) => ({
            objectUrls: {
              ...state.objectUrls,
              [parentId]: urls.filter((u) => u.id !== offlineId),
            },
          }));
          logger.info('Offline evidence object URL revoked after removal', { offlineId, parentId });
        } else {
          logger.info('Offline evidence object URL not found during removal', { offlineId, parentId });
        }
      } catch (error) {
        logger.error('Failed to remove evidence file', error);
        throw error;
      }
    },
  };
});
